import React, { useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BetaBanner from '../components/BetaBanner';
import GoogleAuthButton from '../components/GoogleAuthButton';
import EmailMagicLinkForm from '../components/EmailMagicLinkForm';
import { useAuth } from '../contexts/AuthContext';

export default function Signup() {
  const [search] = useSearchParams();
  const reason = search.get('reason');
  const next = search.get('next');
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      sessionStorage.setItem('post_login_redirect', next);
    }
  }, [next]);

  useEffect(() => {
    if (loading || !user) return;
    let dest;
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      dest = next.startsWith('/admin') && !user.isAdmin ? '/dashboard' : next;
    } else {
      dest = '/dashboard';
    }
    navigate(dest, { replace: true });
  }, [user, loading, next, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col">
      <BetaBanner />
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <div className="text-center">
            <p className="text-[10px] tracking-[0.25em] font-mono gold-text">FREE BETA ACCESS</p>
            <h1 className="font-display mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">
              Create your free<br /><span className="gold-text">Talas beta access.</span>
            </h1>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Sign up with Google, or use any email — we'll send you a one-time sign-in link.
              <span className="block mt-2 text-zinc-300">Talas beta connects to Alpaca paper trading only. No live funds are used.</span>
            </p>
          </div>

          {reason === 'connect_alpaca' && (
            <div className="mt-6 border gold-border bg-[#13110a] rounded-md p-4 text-xs text-zinc-200">
              Sign in to connect your Alpaca paper account.
            </div>
          )}

          <div className="mt-8 border border-white/10 rounded-md p-7 bg-[#0c0c0e]">
            <GoogleAuthButton label="Continue with Google" testid="signup-google-btn" />

            <div className="my-5 flex items-center gap-3" aria-hidden="true">
              <div className="flex-1 h-px bg-white/5" />
              <span className="text-[10px] tracking-[0.25em] font-mono text-zinc-600">OR</span>
              <div className="flex-1 h-px bg-white/5" />
            </div>

            <EmailMagicLinkForm source="signup" next={next} ctaLabel="Email me a sign-up link" />

            <div className="mt-5 pt-5 border-t border-white/5 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
              <ShieldCheck size={12} className="gold-text mt-0.5 shrink-0" strokeWidth={2} />
              <span>By continuing, you agree to our <Link to="/terms-of-use" className="underline hover:gold-text">Terms of Use</Link> and <Link to="/privacy-policy" className="underline hover:gold-text">Privacy Policy</Link>. We never store passwords — sign up with Google or get a one-time link emailed to any address.</span>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-zinc-500">
            Already have access? <Link to="/login" className="gold-text hover:underline">Log in</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
